import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, DoorOpen, Gift, Keyboard, MapPin } from 'lucide-react';
import { EventPositionList } from '../../components/world/EventPositionList';
import { useEventPositions } from '../../hooks/useEventPositions';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { useHashScroll } from '../../hooks/useHashScroll';
import { invasionPolicies } from '../../data/world/invasionPolicies';
import { EXPORT_DATE_LABEL } from '../../data/world/package';
import { bagContents, bagName, levelRange } from '../../utils/bagSources';
import { itemPath, mapName, mapPath } from '../../utils/worldFormat';
import { entryPathById } from '../../utils/guideIndex';
import type { GameEvent, WorldData } from '../../types/world';
const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const pad = (n?: number) => n === undefined ? '--' : String(n).padStart(2, '0');
type Filter = 'activos' | 'todos';
export function EventsGuide() {
  useHashScroll();
  useEffect(() => {
    document.title = 'Eventos e invasiones · Guía MU Vanguard';
  }, []);
  return <div data-id="element-1949" data-mp="1kjob599w2k31">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Eventos'
    }]} eyebrow="Eventos e invasiones" title="Eventos" data-id="element-1950" data-mp="1kh2192zizshn">
        <p className="mt-2 max-w-2xl text-sm md:text-base text-gray-400" data-id="element-1951" data-mp="rur09r80bpkb">
          Horarios, salas y recompensas según la configuración exportada el {EXPORT_DATE_LABEL}. No es información en vivo del servidor.
        </p>
      </WorldPageHeader>
      <WorldGate data-id="element-1952" data-mp="10tm9zda9fhsd">{w => <EventsList w={w} data-id="element-1953" data-mp="10t2pppx50hb6" />}</WorldGate>
    </div>;
}
function isActive(e: GameEvent) {
  return e.activeConfigured !== false;
}
function EventsList({
  w
}: {
  w: WorldData;
}) {
  const [filter, setFilter] = useState<Filter>('activos');
  const list = useMemo(() => Array.from(w.events.values()).filter(e => filter === 'todos' || isActive(e)).sort((a, b) => Number(!isActive(a)) - Number(!isActive(b)) || a.name.localeCompare(b.name, 'es')), [w, filter]);
  if (!w.events.size) return <p className="text-gray-400" data-id="element-1954" data-mp="1bf1hoon3db01">No se pudieron cargar los eventos.</p>;
  return <div data-id="element-1955" data-mp="1q58r6ev4fjol">
      <div role="group" aria-label="Filtrar eventos" className="mb-4 flex gap-1.5" data-id="element-1956" data-mp="1q84nxzv8g2bg">
        {(['activos', 'todos'] as Filter[]).map(f => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${filter === f ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-1957" data-mp="xguuq3jokvf5">
            {f === 'activos' ? 'Activos' : 'Todos'}
          </button>)}
      </div>
      <ul className="grid gap-3 lg:grid-cols-2" data-id="element-1958" data-mp="2hzat4upt8q7l">
        {list.map(e => <EventCard key={e.id} w={w} e={e} data-id="element-1959" data-mp="2duuqd2bhtywl" />)}
      </ul>
    </div>;
}
function EventCard({
  w,
  e
}: {
  w: WorldData;
  e: GameEvent;
}) {
  const [showRewards, setShowRewards] = useState(false);
  const [showPositions, setShowPositions] = useState(false);
  const ep = useEventPositions();
  const eventPos = ep ? ep.positions.filter(p => p.eventId === e.id) : [];
  const schedules = (w.idx.schedulesByEvent.get(e.id) ?? []).slice().sort((a, b) => (a.weekday ?? -1) - (b.weekday ?? -1) || (a.hour ?? 0) - (b.hour ?? 0) || (a.minute ?? 0) - (b.minute ?? 0));
  const rooms = (w.idx.roomsByEvent.get(e.id) ?? []).filter(r => r.active !== false);
  const eventBags = (w.idx.eventBagsByEvent.get(e.id) ?? []).filter(b => b.active !== false);
  const policy = invasionPolicies.find(p => p.eventIds.includes(e.id));
  const isInvasion = (e.type ?? '').toLowerCase().includes('invasion') || e.invasionIndex !== undefined;
  const mapIds = Array.from(new Set([...e.possibleMapIds, ...w.eventMapChoices.filter(c => c.eventId === e.id && c.mapId !== undefined).map(c => c.mapId!)]));
  const bagIds = Array.from(new Set(eventBags.map(b => b.bagId)));
  return <li id={e.id} className="scroll-mt-24 flex flex-col rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 md:p-5" data-id="element-1960" data-mp="1z7t6o1gmscb5">
      <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-1961" data-mp="1u7wydq0whyps">
        <h2 className="font-cinzel text-lg md:text-xl font-bold text-white" data-id="element-1962" data-mp="1p9n4diqwx45r">{e.name}</h2>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${isActive(e) ? 'bg-emerald-400/10 text-emerald-200' : 'bg-white/[0.05] text-gray-400'}`} data-id="element-1963" data-mp="1pbzvaleneouy">
          {isActive(e) ? 'Activo en la configuración' : 'Desactivado en la configuración'}
        </span>
      </div>
      {e.type && <p className="mt-0.5 text-xs text-gray-500" data-id="element-1964" data-mp="2d57yfcucww6a">{isInvasion ? 'Invasión' : e.type}</p>}

      {mapIds.length > 0 && <p className="mt-3 flex flex-wrap items-center gap-1.5 text-sm text-gray-300" data-id="element-1965" data-mp="35nlogq9gbdut">
          <span className="text-gray-400" data-id="element-1966" data-mp="30x59h1pzfk6c">{e.mapSelection === 'random' ? 'Uno de estos mapas:' : 'Mapas:'}</span>
          {mapIds.map(id => <Link key={id} to={mapPath(id)} className="rounded-full border border-white/15 px-2.5 py-0.5 text-xs font-semibold text-gray-200 hover:border-[#D4AF37]/50" data-id="element-1967" data-mp="397488j4632gx">{mapName(w, id)}</Link>)}
        </p>}

      <div className="mt-3" data-id="element-1968" data-mp="3i565qooozczg">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-white" data-id="element-1969" data-mp="3n5kc82anaevd"><Clock className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1970" data-mp="3n3heew393hg3" /> Horario configurado</p>
        {schedules.length ? <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1971" data-mp="mfgbt2x98u">
            {schedules.slice(0, 24).map(s => <li key={s.id} className="rounded-lg bg-white/[0.05] px-2 py-1 font-mono text-xs text-gray-200" data-id="element-1972" data-mp="4r2shuszmsuo">
                {s.weekday !== undefined && WEEKDAYS[s.weekday] ? `${WEEKDAYS[s.weekday].slice(0, 3)} ` : ''}
                {pad(s.hour)}:{pad(s.minute)}
              </li>)}
            {schedules.length > 24 && <li className="px-1 py-1 text-xs text-gray-500" data-id="element-1973" data-mp="jpwtlld6fm4u">+{schedules.length - 24}</li>}
          </ul> : <p className="mt-1 text-sm text-gray-400" data-id="element-1974" data-mp="3q6luu2o1tm2b">Sin horario fijo en la exportación.</p>}
        {isInvasion && <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400" data-id="element-1975" data-mp="3l56cwjh63vo5">
            <Keyboard className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1976" data-mp="3ru6omy8zhov" /> Confírmalo en el juego con la tecla H y en los avisos de Discord.
          </p>}
      </div>

      {eventPos.length > 0 && <div className="mt-3" data-id="element-1977" data-mp="cpcxy92his97">
          <button type="button" onClick={() => setShowPositions(v => !v)} aria-expanded={showPositions} className="flex h-9 items-center gap-1.5 text-sm font-semibold text-white hover:text-[#F3E5AB]" data-id="element-1978" data-mp="7sczokbvmtsn">
            <MapPin className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1979" data-mp="hqk4k4sw5lea" /> Posiciones de monstruos ({eventPos.length})
          </button>
          {showPositions && <EventPositionList w={w} positions={eventPos} max={40} showMonster data-id="element-1980" data-mp="2oh0b04bmrhdk" />}
        </div>}

      {rooms.length > 0 && <div className="mt-3" data-id="element-1981" data-mp="yzmlmnyf5f2b">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-white" data-id="element-1982" data-mp="z0nzf06tkfnq"><DoorOpen className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1983" data-mp="13yg12lx9ng54" /> Entrada por nivel</p>
          <ul className="mt-1.5 grid gap-1 text-sm text-gray-300 sm:grid-cols-2" data-id="element-1984" data-mp="pa32riaphl7g">
            {rooms.map(r => <li key={r.id} className="flex justify-between gap-2 rounded-lg bg-white/[0.03] px-2.5 py-1.5" data-id="element-1985" data-mp="u84mm5fyhojz">
                <span data-id="element-1986" data-mp="g827gqz1998f">Sala {r.roomNumber ?? '—'}</span>
                <span className="tabular-nums text-gray-400" data-id="element-1987" data-mp="gbhlrrkvue8s">
                  {r.levelMin !== undefined ? `Nv. ${r.levelMin}–${r.levelMax ?? '…'}` : 'Nivel no documentado'}
                  {r.masterRequired ? ' · Master' : ''}
                </span>
              </li>)}
          </ul>
        </div>}

      {(bagIds.length > 0 || policy) && <div className="mt-3" data-id="element-1988" data-mp="20phykir6i70z">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-white" data-id="element-1989" data-mp="20n5he7t8w0oz"><Gift className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1990" data-mp="25na26a16rhl0" /> Recompensas</p>
          {policy ? <p className="mt-1 text-sm text-gray-300" data-id="element-1991" data-mp="sjb2wsxcj3ye">
              Consulta las recompensas aprobadas en la{' '}
              <Link to={entryPathById(policy.guideEntryId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1992" data-mp="jowsp4roajm5">ficha de {policy.name}</Link>.
              <span className="mt-0.5 block text-xs text-gray-500" data-id="element-1993" data-mp="enpkx5pj5kfh">La lista de ítems exportada para esta invasión está en revisión por el staff.</span>
            </p> : <>
              <p className="mt-1 text-sm text-gray-400" data-id="element-1994" data-mp="4tqkcb53u8pn">{bagIds.map(id => w.bags.get(id) ? bagName(w, w.bags.get(id)!) : `Bolsa ${id}`).join(' · ')} — cada premio es uno de los resultados posibles.</p>
              <button type="button" onClick={() => setShowRewards(v => !v)} aria-expanded={showRewards} className="mt-1 h-9 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]" data-id="element-1995" data-mp="3ryeqcbtsx35f">
                {showRewards ? 'Ocultar resultados posibles' : 'Ver resultados posibles'}
              </button>
              {showRewards && <ul className="mt-1 flex flex-wrap gap-1.5" data-id="element-1996" data-mp="22n1d89zma1n0">
                  {bagIds.flatMap(id => bagContents(w, id)).filter(c => !c.withheld).slice(0, 60).map(c => <li key={`${c.itemKey}-${c.levelMin ?? ''}`} data-id="element-1997" data-mp="2cde7itx8h96c">
                      <Link to={itemPath(c.itemKey)} className="inline-flex rounded-full border border-white/15 px-2.5 py-1 text-xs text-gray-200 hover:border-[#D4AF37]/50" data-id="element-1998" data-mp="2cckyfmasy7l1">
                        {c.name}{levelRange(c.levelMin, c.levelMax) ? ` ${levelRange(c.levelMin, c.levelMax)}` : ''}
                      </Link>
                    </li>)}
                </ul>}
            </>}
        </div>}

      <p className="mt-auto pt-3 flex items-center gap-1.5 text-[11px] text-gray-500" data-id="element-1999" data-mp="3038hn3q64jmw">
        <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2000" data-mp="344k08pamk4ll" /> Evento documentado en la configuración del {EXPORT_DATE_LABEL} · no comprobado en el juego
      </p>
    </li>;
}